import React from 'react'
import { Select } from 'antd'
import { Trans, useLingui } from '@lingui/react/macro'

import { TTS_ALL_LANGUAGE_OPTIONS } from '../../_constant'

import AdvancedSlider from '@/pages/tad.ai/_components/AdvancedSettings/AdvancedSlider'
import { CssIcon } from '@/components/CssIcon'
import InfoPopover from '@/pages/tad.ai/_components/InfoPopover'


import { cls } from 'tagged-classnames-free';
interface TTSAdvancedSettingsProps {
  stability: number
  speechLanguage: string
  onStabilityChange: (value: number) => void
  onSpeechLanguageChange: (value: string) => void
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

function TTSAdvancedSettings(props: TTSAdvancedSettingsProps) {
  const {
    stability,
    speechLanguage,
    onStabilityChange,
    onSpeechLanguageChange,
    expanded: controlledExpanded,
    onExpandedChange,
  } = props
  const { t } = useLingui()
  const [internalExpanded, setInternalExpanded] = React.useState(false)

  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : internalExpanded
  const setExpanded = (next: boolean) => {
    if (!isControlled) setInternalExpanded(next)
    onExpandedChange?.(next)
  }

  const languageOptions = React.useMemo(
    () => [
      {
        label: t`Default`,
        options: [{ value: 'auto', label: t`Auto` }],
      },
      {
        label: t`All Languages`,
        options: TTS_ALL_LANGUAGE_OPTIONS,
      },
    ],
    [t],
  )

  return (
    <section>
      <button
        type='button'
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
        className={cls`flex w-full items-center justify-between gap-4`}
      >
        <span className={cls`flex items-baseline gap-1.5`}>
          <span className={cls`text-f-text text-lg font-semibold`}>
            <Trans>Advanced Settings</Trans>
          </span>
          <span className={cls`text-f-text-quaternary text-base font-semibold`}>
            <Trans>(Optional)</Trans>
          </span>
        </span>
        <CssIcon
          className={cls`
            text-f-text i-com--down size-4 transition-transform
            ${expanded ? 'rotate-180' : 'rotate-0'}
          `}
        />
      </button>

      {expanded && (
        <div className={cls`flex flex-col gap-4 pt-4`}>
          {/* Speech Language */}
          <div className={cls`flex flex-col gap-2`}>
            <div className={cls`flex items-center gap-1`}>
              <span className={cls`text-f-text-secondary text-base`}>
                <Trans>Speech Language</Trans>
              </span>
              <InfoPopover
                maxWidth={380}
                content={
                  <Trans>
                    Our model usually auto-detects the language from your text
                    input. You can also specify a language if needed, though
                    results may vary.
                  </Trans>
                }
              />
            </div>
            <Select
              value={speechLanguage}
              onChange={onSpeechLanguageChange}
              options={languageOptions}
              className={cls`[&_.coco-select-selector]:!bg-f-bg-elevated w-full`}
              placeholder={t`Select language`}
              variant='outlined'
              popupMatchSelectWidth
              size='large'
              showSearch
              optionFilterProp='label'
              suffixIcon={
                <CssIcon
                  className={cls`text-f-text-tertiary i-com--down size-4`}
                />
              }
              styles={{
                root: {
                  height: 48,
                  borderRadius: 8,
                  fontSize: 16,
                },
              }}
            />
          </div>

          {/* Stability */}
          <div className={cls`flex flex-col gap-2`}>
            <div className={cls`flex items-center gap-1`}>
              <span className={cls`text-f-text-secondary text-base`}>
                <Trans>Stability</Trans>
              </span>
              <InfoPopover
                content={
                  <Trans>
                    Larger parameters result in more stable generated audio, and
                    vice versa.
                  </Trans>
                }
              />
            </div>
            <AdvancedSlider
              value={stability}
              onChange={onStabilityChange}
              step={0.5}
              precision={1}
            />
          </div>
        </div>
      )}
    </section>
  )
}

export default TTSAdvancedSettings
